import React, { useState } from 'react'

export interface RatingWidgetProps {
  min: number
  max: number
  labels?: { low?: string; high?: string }
  value: number | null
  onChange: (value: number) => void
  disabled?: boolean
}

export function RatingWidget({
  min,
  max,
  labels,
  value,
  onChange,
  disabled = false,
}: RatingWidgetProps): React.ReactElement {
  const [hovered, setHovered] = useState<number | null>(null)

  const scalePoints = Array.from(
    { length: max - min + 1 },
    (_, i) => min + i,
  )

  const isActive = (point: number): boolean => {
    if (hovered !== null) return point <= hovered
    if (value !== null) return point <= value
    return false
  }

  return (
    <div data-testid="rating-widget" className="font-sans">
      <div
        role="radiogroup"
        aria-label={`Rating from ${min} to ${max}`}
        className="flex items-center gap-1"
      >
        {scalePoints.map((point) => (
          <button
            key={point}
            type="button"
            role="radio"
            aria-checked={value === point}
            aria-label={`${point} out of ${max}`}
            disabled={disabled}
            className={[
              'flex items-center justify-center rounded-lg border text-sm font-medium transition-colors',
              'w-10 h-10',
              'focus:outline-hidden focus:ring-2 focus:ring-brand-500 focus:ring-offset-2',
              isActive(point)
                ? 'border-brand-500 bg-brand-500 text-neutral-0'
                : 'border-neutral-300 bg-neutral-0 text-neutral-700 hover:border-brand-300 hover:bg-brand-50',
              disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
            ].join(' ')}
            onClick={() => onChange(point)}
            onMouseEnter={() => setHovered(point)}
            onMouseLeave={() => setHovered(null)}
            data-testid={`rating-point-${point}`}
          >
            {point}
          </button>
        ))}
      </div>

      {(labels?.low !== undefined || labels?.high !== undefined) && (
        <div className="flex justify-between mt-2 text-xs text-neutral-500">
          <span data-testid="rating-label-low">
            {labels?.low ?? ''}
          </span>
          <span data-testid="rating-label-high">
            {labels?.high ?? ''}
          </span>
        </div>
      )}
    </div>
  )
}
